import React, { useState } from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Edit, Trash2, ChevronDown, ChevronUp, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BloodSugarHistory = ({ records, onUpdate, onEdit }) => {
  const [showAll, setShowAll] = useState(false);
  const displayRecords = showAll ? records : records.slice(0, 3);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blood-sugar/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Record deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete record');
    }
  };

  const getReadingColor = (reading) => {
    if (reading < 70) return '#F9A7A7'; // Low
    if (reading > 180) return '#F9A7A7'; // High
    return '#A2DAD3'; // Normal
  };

  return (
    <div className="space-y-3">
      {displayRecords.map((record) => (
        <motion.div
          key={record.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 rounded-lg relative"
          style={{ backgroundColor: '#fff5f5', border: '1px solid #F9A7A7' }}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl font-bold" style={{ color: getReadingColor(record.reading) }}>
                  {record.reading}
                </span>
                <span className="text-sm text-gray-600">mg/dL</span>
                {record.meal_context && (
                  <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#F9A7A7', color: 'white' }}>
                    {record.meal_context}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600">
                {format(new Date(record.date), 'MMM d, yyyy')} at {record.time}
              </p>
              {record.notes && (
                <p className="text-xs text-gray-500 mt-1 italic">{record.notes}</p>
              )}
              {record.image_url && record.image_url.trim() !== '' && (
                <div className="mt-2 flex items-center gap-2">
                  <div 
                    className="relative cursor-pointer group"
                    onClick={() => window.open(`${BACKEND_URL}${record.image_url}`, '_blank')}
                  >
                    <img 
                      src={`${BACKEND_URL}${record.image_url}`} 
                      alt="Blood sugar reading" 
                      className="h-16 w-16 object-cover rounded border-2 border-gray-200 group-hover:border-teal-400 transition-all"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="hidden h-16 w-16 items-center justify-center rounded border-2 border-gray-200 bg-gray-100"
                    >
                      <ImageIcon size={24} className="text-gray-400" />
                    </div>
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded transition-all flex items-center justify-center">
                      <span className="text-xs text-white opacity-0 group-hover:opacity-100 font-medium">View</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">Click to view full size</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onEdit(record)}
                className="text-blue-500 hover:text-blue-700 p-1"
                title="Edit"
              >
                <Edit size={14} />
              </button>
              <button
                onClick={() => handleDelete(record.id)}
                className="text-red-500 hover:text-red-700 p-1"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      ))}
      
      {records.length > 3 && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full text-sm py-2 rounded-lg flex items-center justify-center gap-2"
          style={{ color: '#F9A7A7', backgroundColor: '#fff5f5' }}
        >
          {showAll ? (
            <>
              Show Less <ChevronUp size={16} />
            </>
          ) : (
            <>
              Show More ({records.length - 3} more) <ChevronDown size={16} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default BloodSugarHistory;
